import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ArrowLeftRight, Clock } from "lucide-react";

// ── Component ────────────────────────────────────────────────

export function UnixTimestamp() {
    const [timestamp, setTimestamp] = useState(
        Math.floor(Date.now() / 1000).toString(),
    );
    const [dateStr, setDateStr] = useState("");
    const [direction, setDirection] = useState<"ts-to-date" | "date-to-ts">(
        "ts-to-date",
    );
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));
    const [copied, setCopied] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        document.title =
            "Unix Timestamp Converter — Epoch to Date — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Convert Unix timestamps to human-readable dates and back. See the current epoch time, relative time ago, and more. Free online tool.",
            );
        }
    }, []);

    // Live clock
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Math.floor(Date.now() / 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Convert
    useEffect(() => {
        setError(null);
        if (direction === "ts-to-date") {
            if (!timestamp.trim()) {
                setDateStr("");
                return;
            }
            const ts = parseInt(timestamp);
            if (isNaN(ts)) {
                setError("Invalid timestamp");
                return;
            }
            // Handle seconds vs milliseconds
            const ms = ts > 1e12 ? ts : ts * 1000;
            const d = new Date(ms);
            if (isNaN(d.getTime())) {
                setError("Invalid timestamp");
                return;
            }
            setDateStr(d.toISOString());
        } else {
            if (!dateStr.trim()) {
                setTimestamp("");
                return;
            }
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) {
                setError("Invalid date string");
                return;
            }
            setTimestamp(Math.floor(d.getTime() / 1000).toString());
        }
    }, [timestamp, dateStr, direction]);

    const ts = parseInt(timestamp);
    const ms = ts > 1e12 ? ts : ts * 1000;
    const date = isNaN(ms) ? null : new Date(ms);

    function timeAgo(ts: number): string {
        const diff = now - ts;
        if (Math.abs(diff) < 60) return `${Math.abs(diff)}s ${diff > 0 ? "ago" : "from now"}`;
        if (Math.abs(diff) < 3600) return `${Math.floor(Math.abs(diff) / 60)}m ${diff > 0 ? "ago" : "from now"}`;
        if (Math.abs(diff) < 86400) return `${Math.floor(Math.abs(diff) / 3600)}h ${diff > 0 ? "ago" : "from now"}`;
        return `${Math.floor(Math.abs(diff) / 86400)}d ${diff > 0 ? "ago" : "from now"}`;
    }

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Unix Timestamp</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Unix Timestamp Converter</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Convert between Unix timestamps and human-readable dates. Supports seconds and milliseconds.
                    </p>
                </div>

                {/* Live clock */}
                <div className="flex items-center gap-3 mb-8 p-4 bg-[#111113] border border-white/[0.06] rounded-xl">
                    <Clock size={14} className="text-violet-400" />
                    <span className="text-[13px] text-zinc-400">Current Unix time:</span>
                    <code className="text-[15px] text-zinc-200 font-mono font-medium">{now}</code>
                    <button
                        onClick={() => {
                            setDirection("ts-to-date");
                            setTimestamp(now.toString());
                        }}
                        className="ml-auto text-[12px] text-zinc-600 hover:text-zinc-300 transition-colors"
                    >
                        Use now
                    </button>
                </div>

                {/* Direction toggle */}
                <div className="flex items-center gap-3 mb-4">
                    <button
                        onClick={() => setDirection((d) => (d === "ts-to-date" ? "date-to-ts" : "ts-to-date"))}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/[0.06] bg-[#111113] text-[13px] text-zinc-400 hover:text-zinc-200 transition-colors"
                    >
                        <ArrowLeftRight size={14} />
                        {direction === "ts-to-date" ? "Timestamp → Date" : "Date → Timestamp"}
                    </button>
                </div>

                {/* Input fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div>
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Unix Timestamp</span>
                            {timestamp && (
                                <button onClick={() => copy(timestamp, "ts")} className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors">
                                    {copied === "ts" ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                                </button>
                            )}
                        </div>
                        <input
                            value={timestamp}
                            onChange={(e) => {
                                setDirection("ts-to-date");
                                setTimestamp(e.target.value);
                            }}
                            placeholder="1708560000"
                            className="w-full bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[15px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Date (ISO 8601)</span>
                            {dateStr && (
                                <button onClick={() => copy(dateStr, "date")} className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors">
                                    {copied === "date" ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                                </button>
                            )}
                        </div>
                        <input
                            value={dateStr}
                            onChange={(e) => {
                                setDirection("date-to-ts");
                                setDateStr(e.target.value);
                            }}
                            placeholder="2024-02-22T00:00:00.000Z"
                            className="w-full bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[15px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>
                </div>

                {error && <div className="text-[13px] text-red-400 mb-4">{error}</div>}

                {/* Details */}
                {date && !isNaN(date.getTime()) && (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-16">
                        {[
                            ["Local time", date.toLocaleString()],
                            ["UTC", date.toUTCString()],
                            ["ISO 8601", date.toISOString()],
                            ["Relative", timeAgo(Math.floor(date.getTime() / 1000))],
                            ["Day of week", date.toLocaleDateString("en-US", { weekday: "long" })],
                            ["Milliseconds", date.getTime().toString()],
                        ].map(([label, value], i) => (
                            <div key={label} className={`flex items-center justify-between px-4 py-3 text-[13px] ${i < 5 ? "border-b border-white/[0.04]" : ""}`}>
                                <span className="text-zinc-500">{label}</span>
                                <span className="text-zinc-300 font-mono">{value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">What is a Unix timestamp?</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>A Unix timestamp (also called epoch time) is the number of seconds that have elapsed since January 1, 1970, 00:00:00 UTC. It's the standard way to represent time in most programming languages and databases.</p>
                        <p>This converter handles both seconds and milliseconds timestamps, and shows the result in multiple formats including local time, UTC, ISO 8601, and relative time.</p>
                        <p>Everything runs in your browser. Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/jwt-decoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Decoder</Link>
                        <Link to="/tools/uuid-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">UUID Generator</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
